import { useState, useEffect } from "react";
import { getPartite, getMovimentiPartita, deletePartita, bulkDeletePartite } from "@/lib/api";
import { formatDate, formatNumber } from "@/lib/utils";
import { exportPartitaToPDF, exportPartiteListToPDF } from "@/lib/export";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { 
  Package, 
  Search, 
  Eye, 
  Trash2, 
  ArrowDownToLine, 
  ArrowUpFromLine,
  Calendar,
  User,
  Download,
  FileText,
  Filter,
  ArrowUp,
  ArrowDown,
  CheckSquare
} from "lucide-react";
import { toast } from "sonner";
import { motion } from "framer-motion";

export default function Partite() {
  const [partite, setPartite] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // all, active, closed
  const [sortOrder, setSortOrder] = useState("desc"); // desc = più recenti prima, asc = più vecchi prima
  const [selectedPartita, setSelectedPartita] = useState(null);
  const [partitaDetail, setPartitaDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false);

  useEffect(() => {
    loadPartite();
  }, []);

  const loadPartite = async () => {
    try {
      const response = await getPartite();
      setPartite(response.data);
      setSelectedIds([]); // Reset selection on reload
    } catch (error) {
      console.error("Error loading partite:", error);
      toast.error("Errore nel caricamento delle partite");
    } finally {
      setLoading(false);
    }
  };

  const loadPartitaDetail = async (partitaId) => {
    setDetailLoading(true);
    try {
      const response = await getMovimentiPartita(partitaId);
      setPartitaDetail(response.data);
    } catch (error) {
      console.error("Error loading partita detail:", error);
      toast.error("Errore nel caricamento dei dettagli");
    } finally {
      setDetailLoading(false);
    }
  };

  const handleViewDetail = (partita) => {
    setSelectedPartita(partita);
    loadPartitaDetail(partita.id);
  };

  const handleDelete = async (partitaId) => {
    try {
      await deletePartita(partitaId);
      toast.success("Partita eliminata con successo");
      setDeleteConfirm(null);
      loadPartite();
    } catch (error) {
      console.error("Error deleting partita:", error);
      toast.error("Errore nell'eliminazione della partita");
    }
  };

  const handleBulkDelete = async () => {
    try {
      const response = await bulkDeletePartite(selectedIds);
      toast.success(`Eliminate ${response.data.deleted_partite} partite e ${response.data.deleted_movimenti} movimenti`);
      setBulkDeleteConfirm(false);
      loadPartite();
    } catch (error) {
      console.error("Error bulk deleting:", error);
      toast.error("Errore nell'eliminazione");
    }
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === filteredPartite.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredPartite.map(p => p.id));
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const handleExportPDF = () => {
    if (!partitaDetail) return;
    
    exportPartitaToPDF(
      partitaDetail.partita,
      partitaDetail.movimenti,
      {
        totale_entrate: partitaDetail.totale_entrate,
        totale_uscite: partitaDetail.totale_uscite,
        totale_scarto: partitaDetail.totale_scarto || 0,
        saldo: partitaDetail.saldo,
      }
    );
    toast.success("PDF generato con successo");
  };

  const handleExportListPDF = () => {
    const statusLabel = statusFilter === "all" ? "Tutte" : statusFilter === "active" ? "Attive" : "Chiuse";
    exportPartiteListToPDF(filteredPartite, statusLabel);
    toast.success("PDF elenco partite generato");
  };

  // Apply both search and status filter, then sort
  const filteredPartite = partite
    .filter((p) => {
      const matchesSearch = 
        p.codice_partita.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.fornitore.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.prodotto.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesStatus = 
        statusFilter === "all" ||
        (statusFilter === "active" && p.quantita_disponibile > 0) ||
        (statusFilter === "closed" && p.quantita_disponibile <= 0);
      
      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => {
      // Sort by codice_partita
      if (sortOrder === "asc") {
        return a.codice_partita.localeCompare(b.codice_partita);
      } else {
        return b.codice_partita.localeCompare(a.codice_partita);
      }
    });

  // Count for badges
  const activeCount = partite.filter(p => p.quantita_disponibile > 0).length;
  const closedCount = partite.filter(p => p.quantita_disponibile <= 0).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div data-testid="partite-page" className="w-full space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl lg:text-3xl font-bold text-slate-900 tracking-tight">
            Partite
          </h1>
          <p className="text-slate-500 text-sm mt-0.5">
            Gestisci e visualizza tutte le partite registrate
          </p>
        </div>
        <Button 
          onClick={handleExportListPDF}
          disabled={filteredPartite.length === 0}
          className="btn-primary"
          data-testid="export-partite-pdf-btn"
        >
          <Download className="w-4 h-4 mr-2" />
          Esporta PDF
        </Button>
      </div>

      {/* Filters */}
      <Card className="card-industrial">
        <CardContent className="p-3 lg:p-4">
          <div className="flex flex-col sm:flex-row gap-3">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                data-testid="search-partite-input"
                placeholder="Cerca per codice, fornitore o prodotto..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 rounded-lg h-10"
              />
            </div>
            {/* Status Filter */}
            <div className="w-full sm:w-48">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger data-testid="status-filter" className="h-10 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Filter className="w-4 h-4 text-slate-400" />
                    <SelectValue placeholder="Stato" />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">
                    Tutte ({partite.length})
                  </SelectItem>
                  <SelectItem value="active">
                    <span className="flex items-center gap-2">
                      <span className="w-2 h-2 bg-emerald-500 rounded-full"></span>
                      Attive ({activeCount})
                    </span>
                  </SelectItem>
                  <SelectItem value="closed">
                    <span className="flex items-center gap-2">
                      <span className="w-2 h-2 bg-slate-400 rounded-full"></span>
                      Chiuse ({closedCount})
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            {/* Sort Order */}
            <Button
              variant="outline"
              onClick={() => setSortOrder(sortOrder === "desc" ? "asc" : "desc")}
              className="h-10 px-3 rounded-lg whitespace-nowrap"
              data-testid="sort-order-btn"
            >
              {sortOrder === "desc" ? (
                <>
                  <ArrowDown className="w-4 h-4 mr-2" />
                  Più recenti
                </>
              ) : (
                <>
                  <ArrowUp className="w-4 h-4 mr-2" />
                  Più vecchie
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <Card className="card-industrial overflow-hidden">
          <CardHeader className="pb-3 px-4 lg:px-6">
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading text-lg font-bold text-slate-900 flex items-center gap-2">
                <Package className="w-5 h-5 text-orange-500" />
                Elenco Partite ({filteredPartite.length})
              </CardTitle>
              {selectedIds.length > 0 && (
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => setBulkDeleteConfirm(true)}
                  className="h-8"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Elimina {selectedIds.length} selezionate
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {filteredPartite.length > 0 ? (
              <div className="table-scroll-wrapper">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50 border-y border-slate-200">
                      <TableHead className="w-10 text-center">
                        <Checkbox
                          checked={selectedIds.length === filteredPartite.length && filteredPartite.length > 0}
                          onCheckedChange={toggleSelectAll}
                        />
                      </TableHead>
                      <TableHead className="font-heading font-bold text-[11px] uppercase tracking-wider text-slate-500 whitespace-nowrap">
                        Codice
                      </TableHead>
                      <TableHead className="font-heading font-bold text-[11px] uppercase tracking-wider text-slate-500 whitespace-nowrap">
                        Data Entrata
                      </TableHead>
                      <TableHead className="font-heading font-bold text-[11px] uppercase tracking-wider text-slate-500 whitespace-nowrap">
                        Fornitore
                      </TableHead>
                      <TableHead className="font-heading font-bold text-[11px] uppercase tracking-wider text-slate-500 whitespace-nowrap">
                        Prodotto
                      </TableHead>
                      <TableHead className="font-heading font-bold text-[11px] uppercase tracking-wider text-slate-500 text-right whitespace-nowrap">
                        Iniziale
                      </TableHead>
                      <TableHead className="font-heading font-bold text-[11px] uppercase tracking-wider text-slate-500 text-right whitespace-nowrap">
                        Disponibile
                      </TableHead>
                      <TableHead className="font-heading font-bold text-[11px] uppercase tracking-wider text-amber-600 text-right whitespace-nowrap">
                        Scarto
                      </TableHead>
                      <TableHead className="font-heading font-bold text-[11px] uppercase tracking-wider text-slate-500 text-center whitespace-nowrap">
                        Stato
                      </TableHead>
                      <TableHead className="font-heading font-bold text-[11px] uppercase tracking-wider text-slate-500 text-center whitespace-nowrap">
                        Azioni
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredPartite.map((partita, index) => (
                      <TableRow 
                        key={partita.id || index}
                        data-testid={`partita-row-${index}`}
                        className={`hover:bg-slate-50/50 ${selectedIds.includes(partita.id) ? 'bg-orange-50' : ''}`}
                      >
                        <TableCell className="text-center">
                          <Checkbox
                            checked={selectedIds.includes(partita.id)}
                            onCheckedChange={() => toggleSelect(partita.id)}
                          />
                        </TableCell>
                        <TableCell className="font-mono text-sm font-bold text-slate-900">
                          {partita.codice_partita}
                        </TableCell>
                        <TableCell className="font-mono text-sm text-slate-600">
                          {formatDate(partita.data_entrata)}
                        </TableCell>
                        <TableCell className="text-sm text-slate-700">
                          {partita.fornitore}
                        </TableCell>
                        <TableCell className="text-sm text-slate-700">
                          {partita.prodotto}
                        </TableCell>
                        <TableCell className="font-mono text-sm text-slate-600 text-right">
                          {formatNumber(partita.quantita_iniziale)} kg
                        </TableCell>
                        <TableCell className="font-mono text-sm font-bold text-right">
                          <span className={partita.quantita_disponibile > 0 ? "text-emerald-600" : "text-slate-400"}>
                            {formatNumber(partita.quantita_disponibile)} kg
                          </span>
                        </TableCell>
                        <TableCell className="font-mono text-sm font-bold text-amber-600 text-right">
                          {formatNumber(partita.scarto || 0)} kg
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge 
                            variant={partita.quantita_disponibile > 0 ? "default" : "secondary"}
                            className={partita.quantita_disponibile > 0 
                              ? "bg-emerald-100 text-emerald-700 border-emerald-200" 
                              : "bg-slate-100 text-slate-500 border-slate-200"
                            }
                          >
                            {partita.quantita_disponibile > 0 ? "Attiva" : "Esaurita"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="flex items-center justify-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              data-testid={`view-partita-${index}`}
                              onClick={() => handleViewDetail(partita)}
                              className="h-8 w-8 p-0"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              data-testid={`delete-partita-${index}`}
                              onClick={() => setDeleteConfirm(partita)}
                              className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="text-center py-12 px-4 text-slate-400">
                <Package className="w-16 h-16 mx-auto mb-4 opacity-30" />
                <p className="text-lg font-medium">
                  {searchTerm ? "Nessuna partita trovata" : "Nessuna partita registrata"}
                </p>
                <p className="text-sm mt-1">
                  {searchTerm ? "Prova con una ricerca diversa" : "Registra una nuova entrata per creare una partita"}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>

      {/* Detail Dialog */}
      <Dialog open={!!selectedPartita} onOpenChange={() => { setSelectedPartita(null); setPartitaDetail(null); }}>
        <DialogContent className="sm:max-w-[750px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle className="font-heading text-xl font-bold flex items-center gap-2">
                <Package className="w-5 h-5 text-orange-500" />
                Dettaglio Partita {selectedPartita?.codice_partita}
              </DialogTitle>
              {partitaDetail && (
                <Button
                  data-testid="export-partita-pdf-btn"
                  onClick={handleExportPDF}
                  className="btn-primary rounded-sm"
                  size="sm"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Stampa PDF
                </Button>
              )}
            </div>
          </DialogHeader>
          
          {detailLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
            </div>
          ) : partitaDetail ? (
            <div className="space-y-6 mt-4">
              {/* Info Cards */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-500 uppercase tracking-wide">Fornitore</p>
                  <p className="font-medium text-slate-900 mt-1 flex items-center gap-1">
                    <User className="w-4 h-4 text-slate-400" />
                    {partitaDetail.partita.fornitore}
                  </p>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-500 uppercase tracking-wide">Data Entrata</p>
                  <p className="font-mono text-slate-900 mt-1 flex items-center gap-1">
                    <Calendar className="w-4 h-4 text-slate-400" />
                    {formatDate(partitaDetail.partita.data_entrata)}
                  </p>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-500 uppercase tracking-wide">Prodotto</p>
                  <p className="font-medium text-slate-900 mt-1">{partitaDetail.partita.prodotto}</p>
                </div>
              </div>

              {/* Totals Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                  <p className="text-xs text-emerald-600 uppercase tracking-wide font-medium">Tot. Entrate</p>
                  <p className="font-mono font-bold text-emerald-700 text-xl mt-1">
                    +{formatNumber(partitaDetail.totale_entrate)} kg
                  </p>
                </div>
                <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                  <p className="text-xs text-red-600 uppercase tracking-wide font-medium">Tot. Uscite</p>
                  <p className="font-mono font-bold text-red-700 text-xl mt-1">
                    -{formatNumber(partitaDetail.totale_uscite)} kg
                  </p>
                </div>
                <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <p className="text-xs text-amber-600 uppercase tracking-wide font-medium">Tot. Scarto</p>
                  <p className="font-mono font-bold text-amber-700 text-xl mt-1">
                    {formatNumber(partitaDetail.totale_scarto || 0)} kg
                  </p>
                </div>
                <div className="p-4 bg-slate-900 rounded-lg">
                  <p className="text-xs text-slate-400 uppercase tracking-wide font-medium">Disponibile</p>
                  <p className="font-mono font-bold text-white text-xl mt-1">
                    {formatNumber(partitaDetail.saldo)} kg
                  </p>
                </div>
              </div>

              {/* Movements */}
              <div>
                <h3 className="font-heading font-bold text-slate-900 mb-3 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-orange-500" />
                  Storico Movimenti ({partitaDetail.movimenti.length})
                </h3>
                <div className="rounded-sm border border-slate-200 overflow-hidden max-h-64 overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead className="font-heading font-bold text-xs uppercase tracking-wider text-slate-500">
                          Tipo
                        </TableHead>
                        <TableHead className="font-heading font-bold text-xs uppercase tracking-wider text-slate-500">
                          Data
                        </TableHead>
                        <TableHead className="font-heading font-bold text-xs uppercase tracking-wider text-slate-500">
                          Soggetto
                        </TableHead>
                        <TableHead className="font-heading font-bold text-xs uppercase tracking-wider text-slate-500 text-right">
                          Quantità
                        </TableHead>
                        <TableHead className="font-heading font-bold text-xs uppercase tracking-wider text-slate-500">
                          Note
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {partitaDetail.movimenti.map((mov, index) => {
                        const isScarto = mov.tipo === "uscita" && mov.cliente?.toUpperCase() === "SCARTO";
                        return (
                        <TableRow 
                          key={mov.id || index}
                          className={isScarto ? "bg-amber-50 border-l-4 border-l-amber-500" : ""}
                        >
                          <TableCell>
                            <div className={`flex items-center gap-2 ${
                              mov.tipo === "entrata" ? "text-emerald-600" : isScarto ? "text-amber-600" : "text-red-600"
                            }`}>
                              {mov.tipo === "entrata" ? (
                                <ArrowDownToLine className="w-4 h-4" />
                              ) : (
                                <ArrowUpFromLine className="w-4 h-4" />
                              )}
                              <span className="capitalize font-medium">
                                {isScarto ? "Scarto" : mov.tipo}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell className="font-mono text-sm text-slate-600">
                            {formatDate(mov.data)}
                          </TableCell>
                          <TableCell className={`text-sm ${isScarto ? "font-bold text-amber-700" : "text-slate-700"}`}>
                            {mov.tipo === "entrata" ? mov.fornitore : mov.cliente}
                            {isScarto && <span className="ml-2 text-xs bg-amber-200 text-amber-800 px-1.5 py-0.5 rounded">SCARTO</span>}
                          </TableCell>
                          <TableCell className={`font-mono text-sm font-bold text-right ${
                            mov.tipo === "entrata" ? "text-emerald-600" : isScarto ? "text-amber-600" : "text-red-600"
                          }`}>
                            {mov.tipo === "entrata" ? "+" : "-"}{formatNumber(mov.quantita)} kg
                          </TableCell>
                          <TableCell className="text-sm text-slate-500 max-w-[150px] truncate">
                            {mov.note || "-"}
                          </TableCell>
                        </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Conferma Eliminazione</AlertDialogTitle>
            <AlertDialogDescription>
              Sei sicuro di voler eliminare la partita <span className="font-mono font-bold">{deleteConfirm?.codice_partita}</span>?
              <br />
              Questa azione eliminerà anche tutti i movimenti associati e non può essere annullata.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Annulla</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-btn"
              onClick={() => handleDelete(deleteConfirm?.id)}
              className="bg-red-600 hover:bg-red-700 rounded-sm"
            >
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Delete Confirmation */}
      <AlertDialog open={bulkDeleteConfirm} onOpenChange={setBulkDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-red-600">Eliminazione Multipla</AlertDialogTitle>
            <AlertDialogDescription>
              Stai per eliminare <span className="font-bold">{selectedIds.length} partite</span> e tutti i loro movimenti associati.
              <br /><br />
              <span className="text-red-600 font-medium">Questa azione non può essere annullata!</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkDelete}
              className="bg-red-600 hover:bg-red-700 rounded-sm"
            >
              Elimina {selectedIds.length} partite
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
